import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAdminSummary,
  useGetSiteManagerMonitoring,
  useGetProjectHealth,
  useGetActiveProjects,
  useGetActionQueue,
  useGetSafetyCompliance,
  useGetQualityControl,
  useGetSafetyAuditSummary,
  useGetQualityAuditSummary,
  useGetProjectPlanByStatus,
  useGetUpcomingEvents,
  useGetClientPhotos,
  useGetScheduleVariance,
  useGetOverview,
  useGetCategoryPerformance,
  useGetClosureReadiness,
  useGetWorkforceOverview,
  useReviewAttachment,
  useDeleteClientAttachment,
} from "@/services";

import { useNavigate } from "react-router-dom";
import { toast } from "@/components";



import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TITLE,
  VALUE,
  SUBTITLE,
  TYPE,
  NAME,
  PROGRESS,
  STATUS,
  QC_OFFICERS,
  SAFETY_OFFICERS,
  SITE_MANAGERS,
  PROJECT_PLANS,
  REVISIONS,
  PROJECT_CLOSURE,
  PROJECT_MANAGERS,
  CATEGORY,
  DELAYED,
  SAFETY,
  QUALITY,

  TOTAL_PHOTOS,
  PENDING_APPROVAL,
  APPROVED,
  REJECTED,
  PROJECT_LIST,
} from "./consts";
import { schema, formValues } from "./validations";
import {
  getRequiredFields,
  getProjectHealthKey,
} from "@/lib/utils";

type HealthFilter = "on_track" | "delayed" | "critical";

const mapHealthStatus = (status: string) => {
  return getProjectHealthKey(status);
};

const useDashboard = () => {
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();


  const [isPhotoSliderOpen, setIsPhotoSliderOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: rawAdminSummaryData, isLoading: isAdminSummaryLoading } = useGetAdminSummary();
  const { data: rawActiveProjectsApiData, isLoading: isActiveProjectsLoading } = useGetActiveProjects();
  const { data: rawProjectHealthApiData, isLoading: isProjectHealthLoading } = useGetProjectHealth();
  const { data: rawActionQueueApiData, isLoading: isActionQueueLoading } = useGetActionQueue();
  const { data: rawSafetyComplianceData, isLoading: isSafetyComplianceLoading } = useGetSafetyCompliance();
  const { data: rawQualityControlData, isLoading: isQualityControlLoading } = useGetQualityControl();


  const projectList = useMemo(() => {
    const list = Array.isArray(rawActiveProjectsApiData) ? rawActiveProjectsApiData : rawActiveProjectsApiData?.data;
    if (!list) return { data: [] };
    return {
      data: list.map((p: any) => ({
        ...p,
        name: p.projectName,
        projectId: p.projectId,
        status: mapHealthStatus(p.healthStatus),
        projectPlanVersion: p.planVersion
      }))
    };
  }, [rawActiveProjectsApiData]);

  const isProjectListLoading = isActiveProjectsLoading;

  const [keys, setKeys] = useState<any>({
    [PROJECT_LIST]: Math.random(),
  });

  const requiredFields = getRequiredFields(schema);

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [PROJECT_LIST]: "",
    },
  });

  const selectedProjectId = form.watch(PROJECT_LIST);

  useEffect(() => {
    if (!selectedProjectId && projectList?.data?.length > 0) {
      form.setValue(PROJECT_LIST, String(projectList.data[0].projectId), {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [projectList, selectedProjectId, form]);

  const selectedProject = useMemo(() => {
    return (
      projectList?.data?.find(
        (project: any) =>
          String(project.projectId) === String(selectedProjectId)
      ) || null
    );
  }, [projectList, selectedProjectId]);

  const { data: rawSiteManagerMonitoringData, isLoading: isSiteManagerMonitoringLoading } = useGetSiteManagerMonitoring({ projectId: selectedProjectId });
  const { data: rawSafetyAuditSummaryData, isLoading: isSafetyAuditSummaryLoading } = useGetSafetyAuditSummary({ projectId: selectedProjectId });
  const { data: rawQualityAuditSummaryData, isLoading: isQualityAuditSummaryLoading } = useGetQualityAuditSummary({ projectId: selectedProjectId });

  const { data: approvedPlanData, isFetching: isApprovedPlanFetching } = useGetProjectPlanByStatus(selectedProjectId, "APPROVED");

  const { data: upcomingEventsData, isLoading: isUpcomingEventsLoading } = useGetUpcomingEvents({ projectId: selectedProjectId });
  const { data: clientPhotosData, isLoading: isClientPhotosLoading } = useGetClientPhotos({ projectId: selectedProjectId });
  const { data: scheduleVarianceData, isLoading: isScheduleVarianceLoading } = useGetScheduleVariance({ projectId: selectedProjectId });
  const { data: overviewData, isLoading: isOverviewLoading } = useGetOverview({ projectId: selectedProjectId });
  const { data: categoryPerformanceData, isLoading: isCategoryPerformanceLoading } = useGetCategoryPerformance({ projectId: selectedProjectId });
  const { data: closureReadinessData, isLoading: isClosureReadinessLoading } = useGetClosureReadiness({ projectId: selectedProjectId });
  const { data: rawWorkforceOverviewData, isLoading: isWorkforceOverviewLoading } = useGetWorkforceOverview();

  const { mutate: reviewAttachment, isPending: isReviewingAttachment } = useReviewAttachment({
    onSuccess: () => {
      toast({ message: "Status Updated Successfully" });
      setIsPhotoSliderOpen(false);
    },
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
    }
  });

  const { mutate: deleteAttachment, isPending: isDeletingAttachment } = useDeleteClientAttachment({
    onSuccess: () => {
      toast({ message: "Attachment Deleted Successfully" });
      setIsPhotoSliderOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["dashboard-pm-client-photos", selectedProjectId],
      });
    },
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
    }
  });

  const handleReviewPhoto = (photo: any, action: "APPROVED" | "REJECTED" | "DELETE", reason?: string) => {
    if (action === "DELETE") {
      deleteAttachment({
        projectId: selectedProjectId,
        taskId: photo.taskId,
        attachmentId: photo.taskAttachmentId
      });
      return;
    }
    reviewAttachment({
      projectId: selectedProjectId,
      taskId: photo.taskId,
      attachmentId: photo.taskAttachmentId,
      payload: {
        action: action as "APPROVED" | "REJECTED",
        rejectionReason: reason
      }
    });
    console.log("attachmentId", photo.taskAttachmentId)
  };

  const handleMajorCountClick = () => {
    if (selectedProjectId && approvedPlanData?.projectPlanId) {
      navigate(
        `/admin/project-overview/${selectedProjectId}/edit/project-plan/${approvedPlanData.projectPlanId}/view`
      );
    } else if (selectedProjectId) {
      toast({ message: "No approved plan found for this project.", type: "error" });
      console.warn("No approved plan found for project:", selectedProjectId);
    }
  };



  const projectPlanVersion = selectedProject?.projectPlanVersion;
  const adminSummaryData = rawAdminSummaryData?.data || rawAdminSummaryData;
  const siteManagerMonitoringData = rawSiteManagerMonitoringData?.data || rawSiteManagerMonitoringData;
  const projectHealthApiData = rawProjectHealthApiData?.data || rawProjectHealthApiData;
  const activeProjectsApiData = rawActiveProjectsApiData?.data || rawActiveProjectsApiData;
  const actionQueueApiData = rawActionQueueApiData?.data || rawActionQueueApiData;
  const workforceOverviewApiData = rawWorkforceOverviewData?.data || rawWorkforceOverviewData;

  const upcomingEventsDataRaw = upcomingEventsData?.data || upcomingEventsData;
  const clientPhotosDataRaw = clientPhotosData?.data || clientPhotosData;
  const scheduleVarianceDataRaw = scheduleVarianceData?.data || scheduleVarianceData;
  const overviewDataRaw = overviewData?.data || overviewData;
  const categoryPerformanceDataRaw = categoryPerformanceData?.data || categoryPerformanceData;
  const closureReadinessDataRaw = closureReadinessData?.data || closureReadinessData;

  const statsCards = [
    {
      [TITLE]: "Active Projects",
      [VALUE]: adminSummaryData?.activeProjects ?? 0,
      [SUBTITLE]: "Across all regions",
      [TYPE]: "active",
    },
    {
      [TITLE]: "Projects at Risk",
      [VALUE]: adminSummaryData?.projectsAtRisk ?? 0,
      [SUBTITLE]: "Schedule / Compliance",
      [TYPE]: "risk",
    },
    {
      [TITLE]: "Pending Approvals",
      [VALUE]: adminSummaryData?.pendingApprovals ?? 0,
      [SUBTITLE]: "Admin action required",
      [TYPE]: "approval",
    },
    {
      [TITLE]: "Overall Completion",
      [VALUE]: adminSummaryData?.overallCompletion != null ? `${adminSummaryData.overallCompletion}%` : "0%",
      [SUBTITLE]: "Weighted portfolio progress",
      [TYPE]: "completion",
    },
  ];

  const safetyCompliance = rawSafetyComplianceData?.data || adminSummaryData?.safetyCompliance || {
    majorNonCompliance: 0,
    minorNonCompliance: 0,
  };

  const qualityControl = rawQualityControlData?.data || adminSummaryData?.qualityControl || {
    minorFailure: 0,
    majorFailure: 0,
  };

  const safetyAuditSummary = rawSafetyAuditSummaryData?.data || {
    majorNonCompliance: 0,
    minorNonCompliance: 0,
  };

  const qualityAuditSummary = rawQualityAuditSummaryData?.data || {
    minorFailure: 0,
    majorFailure: 0,
  };


  const adminQueue = {
    [PROJECT_PLANS]: actionQueueApiData?.pendingApprovals?.length ?? 0,
    [REVISIONS]: actionQueueApiData?.scheduleRevisions?.length ?? 0,
    [PROJECT_CLOSURE]: actionQueueApiData?.closureEligible?.length ?? 0,
  };

  const workforceOverview = useMemo(() => {
    const defaultWorkforce = {
      [PROJECT_MANAGERS]: 0,
      [SITE_MANAGERS]: 0,
      [SAFETY_OFFICERS]: 0,
      [QC_OFFICERS]: 0,
    };

    if (!workforceOverviewApiData?.roles) return defaultWorkforce;

    const roles = workforceOverviewApiData.roles;
    const findCount = (code: string) => roles.find((r: any) => r.roleCode === code)?.userCount ?? 0;

    return {
      [PROJECT_MANAGERS]: findCount("PROJECT_MANAGER"),
      [SITE_MANAGERS]: findCount("SITE_MANAGER"),
      [SAFETY_OFFICERS]: findCount("SAFETY_OFFICER"),
      [QC_OFFICERS]: findCount("QUALITY_OFFICER"),
    };
  }, [workforceOverviewApiData]);

  const [timeRange, setTimeRange] = useState<"week" | "month" | "quarter">(
    "week"
  );

  const [selectedHealthFilter, setSelectedHealthFilter] =
    useState<HealthFilter>("on_track");

  const projectHealthData = useMemo(() => {
    const data = projectHealthApiData || {};
    return {
      week: {
        on_track: data.onTrack || 0,
        delayed: data.delayed || 0,
        critical: data.critical || 0,
      },
      month: {
        on_track: data.onTrack || 0,
        delayed: data.delayed || 0,
        critical: data.critical || 0,
      },
      quarter: {
        on_track: data.onTrack || 0,
        delayed: data.delayed || 0,
        critical: data.critical || 0,
      },
    };
  }, [projectHealthApiData]);

  const activeProjectsList = Array.isArray(activeProjectsApiData) ? activeProjectsApiData : activeProjectsApiData?.data;

  const activeProjects = activeProjectsList?.length > 0 
    ? activeProjectsList.map((p: any) => ({
        [NAME]: p.projectName,
        [PROGRESS]: p.progressPercent,
        [STATUS]: mapHealthStatus(p.healthStatus),
      }))
    : [];

  const filteredActiveProjects = useMemo(() => {
    return activeProjects.filter(
      (project: any) => project[STATUS] === selectedHealthFilter
    );
  }, [activeProjects, selectedHealthFilter]);

  const overallProgress = [
    {
      [TITLE]: "Overall Completion",
      [VALUE]: overviewDataRaw?.overallCompletion != null ? `${overviewDataRaw.overallCompletion}%` : "0%",
      [TYPE]: "overall",
    },
    {
      [TITLE]: "Total Tasks",
      [VALUE]: overviewDataRaw?.totalTasks ?? 0,
      [TYPE]: "total",
    },
    {
      [TITLE]: "Delayed Tasks",
      [VALUE]: overviewDataRaw?.delayedTasks ?? 0,
      [TYPE]: "delayed",
    },
    {
      [TITLE]: "Blocked-Safety",
      [VALUE]: overviewDataRaw?.blockedSafety ?? 0,
      [TYPE]: "blocked",
    },
    {
      [TITLE]: "Blocked-Quality",
      [VALUE]: overviewDataRaw?.blockedQuality ?? 0,
      [TYPE]: "quality",
    },
  ];

  const categoryPerformance = (Array.isArray(categoryPerformanceDataRaw) ? categoryPerformanceDataRaw : [])?.map((item: any) => ({
    [CATEGORY]: item.categoryName,
    [PROGRESS]: item.completionPercent,
    [SUBTITLE]: item.lastUpdatedAt ? `Last Updated: ${new Date(item.lastUpdatedAt).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}` : "No Update Found",
    [DELAYED]: item.delayedTasks,
    [SAFETY]: item.blockedSafety,
    [QUALITY]: item.blockedQuality,
  })) || [];


  const upcomingEvents = (Array.isArray(upcomingEventsDataRaw) ? upcomingEventsDataRaw : [])?.map((event: any) => ({
    ...event,
    formattedDate: new Date(event.eventDate).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    })
  })) || [];

  const scheduleVariance = scheduleVarianceDataRaw || {
    plannedEndDate: null,
    projectedEndDate: null,
    varianceDays: 0
  };

  const closureReadiness = closureReadinessDataRaw || {
    allTasksComplete: false,
    allSafetyAuditsClosed: false,
    allQualityAuditsClosed: false,
    canClose: false
  };

  const clientSharedPhotos = useMemo(() => {
    if (!clientPhotosDataRaw || !Array.isArray(clientPhotosDataRaw)) return [];
    
    // Group photos by taskId or something?
    // The previous implementation expected categories.
    // If we don't have categories in this API, maybe we show them as "Shared Photos"
    
    return [
      {
        [CATEGORY]: "Shared Photos",
        [TOTAL_PHOTOS]: clientPhotosDataRaw.length,
        [PENDING_APPROVAL]: clientPhotosDataRaw.filter((p: any) => (p.approvalStatus || p.status) === "PENDING").length,
        [APPROVED]: clientPhotosDataRaw.filter((p: any) => (p.approvalStatus || p.status) === "APPROVED").length,
        [REJECTED]: clientPhotosDataRaw.filter((p: any) => (p.approvalStatus || p.status) === "REJECTED").length,
        photos: clientPhotosDataRaw.map((p: any) => ({
          url: p.presignedUrl,
          description: p.description || "description not found",
          taskId: p.taskId,
          taskAttachmentId: p.taskAttachmentId,
          approvalStatus: p.approvalStatus || p.status,
          reviewedByName: p.reviewedByName,
          reviewedAt: p.reviewedAt,
          rejectionReason: p.rejectionReason,
        }))
      }
    ];
  }, [clientPhotosDataRaw]);

  const siteManagerLastUpdate = useMemo(() => {
    const timestamp = siteManagerMonitoringData?.lastUpdateTimestamp;
    if (!timestamp) return "No Update Found";
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return timestamp;
    }
  }, [siteManagerMonitoringData]);

  const selectedGroup = useMemo(() => {
    return clientSharedPhotos?.find((item: any) => item[CATEGORY] === selectedCategory);
  }, [clientSharedPhotos, selectedCategory]);

  const selectedPhotos = selectedGroup?.photos || [];

  return {
    loggedInUserData,
    statsCards,
    safetyCompliance,
    qualityControl,
    adminQueue,
    workforceOverview,
    timeRange,
    setTimeRange,
    projectHealthData,
    activeProjects,
    filteredActiveProjects,
    selectedHealthFilter,
    setSelectedHealthFilter,
    overallProgress,
    categoryPerformance,
    projectList,
    isProjectListLoading,
    selectedProjectId,
    selectedProject,
    clientSharedPhotos,
    siteManagerLastUpdate,
    upcomingEvents,
    scheduleVariance,
    closureReadiness,
    keys,
    requiredFields,
    setKeys,
    form,
    isAdminSummaryLoading,
    isSiteManagerMonitoringLoading,
    isProjectHealthLoading,
    isActiveProjectsLoading,
    isActionQueueLoading,
    isSafetyComplianceLoading,
    isQualityControlLoading,
    isSafetyAuditSummaryLoading,
    isQualityAuditSummaryLoading,
    isUpcomingEventsLoading,
    isClientPhotosLoading,
    isScheduleVarianceLoading,
    isOverviewLoading,
    isCategoryPerformanceLoading,
    isClosureReadinessLoading,
    isWorkforceOverviewLoading,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    selectedCategory,
    setSelectedCategory,
    selectedPhotos,
    safetyAuditSummary,
    qualityAuditSummary,
    handleMajorCountClick,
    handleReviewPhoto,
    isReviewingAttachment,
    isDeletingAttachment,
    isApprovedPlanLoading: isApprovedPlanFetching,
    projectPlanVersion
  };
};

export default useDashboard;